import {
  getGridNumericOperators,
  getGridStringOperators,
  getGridBooleanOperators,
} from '@mui/x-data-grid-pro';
import { GridFilterOperator } from '@mui/x-data-grid-pro';
import { CustomFilterOperator } from '../columnsFactory/customFilters/operators/constants';

export enum GridFilterValue {
  CONTAINS = 'contains',
  NOT_CONTAINS = 'doesNotContain',
  EQUALS = 'equals',
  NOT_EQUALS = 'doesNotEqual',
  STARTS_WITH = 'startsWith',
  ENDS_WITH = 'endsWith',
  IS_EMPTY = 'isEmpty',
  IS_NOT_EMPTY = 'isNotEmpty',
  IS_ANY_OF = 'isAnyOf',
  EQUAL = '=',
  NOT_EQUAL = '!=',
  GREATER_THAN = '>',
  LESS_THAN = '<',
  GREATER_EQUAL = '>=',
  LESS_EQUAL = '<=',
  IS = 'is',
  NOT = 'not',
}

export const GRID_STRING_FILTER_VALUES = [
  GridFilterValue.CONTAINS,
  GridFilterValue.NOT_CONTAINS,
  GridFilterValue.EQUALS,
  GridFilterValue.NOT_EQUALS,
  GridFilterValue.STARTS_WITH,
  GridFilterValue.ENDS_WITH,
  GridFilterValue.IS_EMPTY,
  GridFilterValue.IS_NOT_EMPTY,
  GridFilterValue.IS_ANY_OF,
] as const;

export enum GridFilterValueType {
  NUMERIC = 'numeric',
  STRING = 'string',
  DATE = 'date',
  BOOLEAN = 'boolean',
  SINGLE_SELECT = 'singleSelect',
  MULTI_SELECT = 'multiSelect',
}

type GridOperatorValue = GridFilterOperator['value'];

const NUMERIC_FILTER_OPERATORS = [
  GridFilterValue.EQUAL,
  GridFilterValue.EQUALS,
  GridFilterValue.NOT_EQUAL,
  GridFilterValue.NOT_EQUALS,
  GridFilterValue.GREATER_THAN,
  GridFilterValue.LESS_THAN,
  GridFilterValue.GREATER_EQUAL,
  GridFilterValue.LESS_EQUAL,
  'gt',
  'lt',
  'gte',
  'lte',
] as const;

const STRING_FILTER_OPERATORS = [
  GridFilterValue.CONTAINS,
  GridFilterValue.NOT_CONTAINS,
  GridFilterValue.EQUALS,
  GridFilterValue.NOT_EQUALS,
  GridFilterValue.STARTS_WITH,
  GridFilterValue.ENDS_WITH,
  GridFilterValue.IS_EMPTY,
  GridFilterValue.IS_NOT_EMPTY,
  GridFilterValue.IS_ANY_OF,
  GridFilterValue.IS,
  GridFilterValue.NOT,
] as const;

const DATE_FILTER_OPERATORS = [
  CustomFilterOperator.DATE_RANGE,
  CustomFilterOperator.DATE_RANGE_PICKER,
  GridFilterValue.IS_EMPTY,
  GridFilterValue.IS_NOT_EMPTY,
] as const;

const BOOLEAN_FILTER_OPERATORS = [
  GridFilterValue.IS,
  GridFilterValue.NOT,
] as const;

const SINGLE_SELECT_FILTER_OPERATORS = [GridFilterValue.IS] as const;

const MULTI_SELECT_FILTER_OPERATORS = [
  CustomFilterOperator.CONTAINS,
  GridFilterValue.IS_EMPTY,
  GridFilterValue.IS_NOT_EMPTY,
] as const;

export type NumericFilterOperator =
  (typeof NUMERIC_FILTER_OPERATORS)[number];
export type StringFilterOperator =
  (typeof STRING_FILTER_OPERATORS)[number];
export type DateFilterOperator = (typeof DATE_FILTER_OPERATORS)[number];
export type BooleanFilterOperator =
  (typeof BOOLEAN_FILTER_OPERATORS)[number];
export type SingleSelectFilterOperator =
  (typeof SINGLE_SELECT_FILTER_OPERATORS)[number];
export type MultiSelectFilterOperator =
  (typeof MULTI_SELECT_FILTER_OPERATORS)[number];

const supportedOperatorsByValueType: Record<
  GridFilterValueType,
  readonly GridOperatorValue[]
> = {
  [GridFilterValueType.NUMERIC]: getGridNumericOperators()
    .map(({ value }) => value)
    .filter(isNumericFilterOperator),
  [GridFilterValueType.STRING]: getGridStringOperators()
    .map(({ value }) => value)
    .filter(isStringFilterOperator),
  [GridFilterValueType.DATE]: DATE_FILTER_OPERATORS,
  [GridFilterValueType.BOOLEAN]: getGridBooleanOperators()
    .map(({ value }) => value)
    .filter(isBooleanFilterOperator),
  [GridFilterValueType.SINGLE_SELECT]: SINGLE_SELECT_FILTER_OPERATORS,
  [GridFilterValueType.MULTI_SELECT]: MULTI_SELECT_FILTER_OPERATORS,
};

export function isNumericFilterOperator(
  operator: GridOperatorValue
): operator is NumericFilterOperator {
  return NUMERIC_FILTER_OPERATORS.includes(operator as NumericFilterOperator);
}

export function isStringFilterOperator(
  operator: GridOperatorValue
): operator is StringFilterOperator {
  return STRING_FILTER_OPERATORS.includes(operator as StringFilterOperator);
}

export function isDateFilterOperator(
  operator: GridOperatorValue
): operator is DateFilterOperator {
  return DATE_FILTER_OPERATORS.includes(operator as DateFilterOperator);
}

export function isBooleanFilterOperator(
  operator: GridOperatorValue
): operator is BooleanFilterOperator {
  return BOOLEAN_FILTER_OPERATORS.includes(operator as BooleanFilterOperator);
}

export function isSingleSelectFilterOperator(
  operator: GridOperatorValue
): operator is SingleSelectFilterOperator {
  return SINGLE_SELECT_FILTER_OPERATORS.includes(
    operator as SingleSelectFilterOperator
  );
}

export function isMultiSelectFilterOperator(
  operator: GridOperatorValue
): operator is MultiSelectFilterOperator {
  return MULTI_SELECT_FILTER_OPERATORS.includes(
    operator as MultiSelectFilterOperator
  );
}

export function isOperatorSupportedForValueType(
  type: GridFilterValueType,
  operator: GridOperatorValue
): boolean {
  return supportedOperatorsByValueType[type].includes(operator);
}
